import React, { useEffect, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ReactComponent as DemandForecasting } from "../../icons/dashboard/demandForecasting.svg";
import { ReactComponent as JvvnlIcon } from "../../icons/jvvnl.svg";
import { getSessionValidity } from "../../Rest_api/RestApi";
import { COLOR_CODES, isMobile } from "../../common";
import { ReactComponent as AnalyticsIcon } from "../../icons/analytics.svg";
import { ReactComponent as AnalyticsSelectedIcon } from "../../icons/analytics-selected.svg";
import { ReactComponent as DashboardIcon } from "../../icons/dashboard.svg";
import { ReactComponent as DashboardSelectedIcon } from "../../icons/dashboard-selected.svg";
import { DynamicDatePicker } from "../../components/DatePicker";
import Hamburger from "../../components/Hamburger";
import { ReactComponent as GNAiIcon } from "../../icons/gnai_logo.svg";
import { ReactComponent as RajAgentIcon } from "../../icons/gnai.svg";
import { ReactComponent as RajAgentSelectedIcon } from "../../icons/gnai-active.svg";
import { redirectToLogin } from "../Protected";
import { SquarePen } from "lucide-react";
import { IconTab } from "../../components/IconTab";
import { PageHeader } from "../../components/Page";

function JvvnlDashboardItem({
  title,
  Icon,
  to,
}: {
  title: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  to: string;
}) {
  return (
    <NavLink to={`${to}`} className="dashboard-item">
      <Icon
        width={80}
        height={33}
        style={{
          fill: COLOR_CODES.primary,
        }}
      />
      <p>{title}</p>
    </NavLink>
  );
}

export function JvvnlTitle() {
  return (
    <div className="flex flex-row justify-center items-center space-x-2 ">
      <JvvnlIcon width={"40px"} height={"60px"} />
      <h1 className="text-[#6F6D7C] text-[18px]">
        JVVNL Power Analytics Center
      </h1>
    </div>
  );
}

export default function JvvnlDashboard() {
  // Clear some localStorage on mount and validate session
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshSession = () => {
    setRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    localStorage.removeItem("procurement_edit_id");
    getSessionValidity().then((res) => {
      if (res.status === false) {
        redirectToLogin(window.location.href);
      }
    });
  }, []);

  // Use searchParams to get and set the 'tab' query parameter
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "dashboard";
  const [selectedTab, setSelectedTab] = useState(initialTab);

  // Sync state with query parameter changes
  useEffect(() => {
    const tab = searchParams.get("tab") || "dashboard";
    if (tab !== selectedTab) {
      setSelectedTab(tab);
    }
  }, [searchParams, selectedTab]);

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setSearchParams({ tab });
  };

  const [dates, setDate] = useState({
    startDate: new Date("2025-01-06"),
    endDate: new Date("2025-01-13"),
    key: "selection",
  });

  return (
    <div className="page-wrapper">
      <PageHeader
        leftComponent={
          <div className="flex flex-row md:gap-x-4 gap-x-2 items-center">
            <GNAiIcon width={80} />
            {!isMobile && <Hamburger />}

            {selectedTab === "vidyutai" && !isMobile && (
              <h1 className="text-[18px] text-[#6F6D7C]">Vidyut AI</h1>
            )}
            {isMobile && (
              <h1 className="text-xl font-semibold tracking-wide text-gray-800">
                Vidyut AI - JVVNL
              </h1>
            )}
          </div>
        }
        middleComponent={!isMobile ? <JvvnlTitle /> : undefined}
        rightComponent={
          <div className="md:min-w-[300px] flex justify-end">
            {selectedTab === "analytics" && (
              <DynamicDatePicker
                setDate={(date) => setDate(date[0]!)}
                defaultDates={dates}
              />
            )}
            {selectedTab === "vidyutai" && (
              <button
                onClick={refreshSession}
                title="Start New Chat"
                className="p-2 rounded hover:bg-gray-100 transition"
              >
                <SquarePen size={22} />
              </button>
            )}
          </div>
        }
      />

      <div className="dashboard-item-container">
        <div className="flex flex-col justify-between h-full">
          <div className="flex flex-row flex-wrap gap-5 justify-center">
            <JvvnlDashboardItem
              Icon={DemandForecasting}
              title="Overview"
              to="overview"
            />
            {/* <JvvnlDashboardItem
              Icon={DemandForecasting}
              title="Demand"
              to="demand"
            /> */}
            {/* <JvvnlDashboardItem
              Icon={DemandForecasting}
              title="Generation"
              to="generation"
            /> */}
            <JvvnlDashboardItem
              Icon={DemandForecasting}
              title="Forecast Prediction"
              to="forecast"
            />
          </div>
          <div className="gna-footer-container mt-8">
            <div className="grid grid-cols-3 justify-between items-center gap-5 ">
              <h3 className="text-[#9f9f9f] font-medium text-[13px]">
                GNA Energy Data and Analytics Capability Center (GDACC)
              </h3>
              <p className="gna-footer-text text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text  text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const TabSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Read the current 'tab' query parameter from the URL.
  // Default to "analytics" if no tab is specified.
  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get("tab") || "dashboard";

  // When a tab is clicked, update the URL accordingly.
  const handleTabChange = (newTab: string) => {
    navigate(`/ruvnl?tab=${newTab}`);
  };

  return (
    <IconTab
      tabs={[
        {
          key: "analytics",
          icon: AnalyticsIcon,
          selectedIcon: AnalyticsSelectedIcon,
        },
        {
          key: "dashboard",
          icon: DashboardIcon,
          selectedIcon: DashboardSelectedIcon,
        },
        {
          key: "vidyutai",
          icon: RajAgentIcon,
          selectedIcon: RajAgentSelectedIcon,
        },
      ]}
      selected={tab}
      onChange={handleTabChange}
    />
  );
};
